/**
 * SPRING Stage 1 Assessment — Question Definitions
 * Per Build Spec v2 (Appendix A), locked 30 October 2026.
 *
 * 4 Sections, 31 questions (plus conditionals).
 * Every question, answer option and label is verbatim from the spec.
 */

export const ASSESSMENT_SECTIONS = [
  { id: 1, name: "Let's Start With You", count: 11 },
  { id: 2, name: 'Mindset and Motivation', count: 14 },
  { id: 3, name: 'Financial Situation', count: 8 },
  { id: 4, name: 'Skills Inventory', count: 4 },
];

const QUESTIONS = [
  // ====================================================================
  // SECTION 1 — Let's Start With You
  // ====================================================================
  {
    id: 'Q1', section: 1, type: 'name',
    question: 'What is your full name?',
    fields: [
      { key: 'full_name', label: 'What is your full name?', required: true },
      { key: 'preferred_name', label: 'What would you like us to call you?', required: true },
      { key: 'email', label: 'Your email address', required: true, inputType: 'email' },
    ],
    afterMessage: 'Got it, [preferred_name]. Let\'s get started.',
  },
  {
    id: 'Q1a', section: 1, type: 'dropdown', conditional: false,
    question: 'Where are you based?',
    subtitle: 'So we can use the right local words and information for you.',
    options: [
      'New Zealand',
      'Australia',
      'United Kingdom',
      'Somewhere else',
    ],
    hasTextBox: { trigger: 'Somewhere else', placeholder: 'Which country?' },
  },
  {
    id: 'Q1b', section: 1, type: 'single', optional: true,
    question: 'Which age group are you in?',
    subtitle: 'This helps us improve SPRING for people at every stage of life. It never affects your results.',
    options: [
      'Under 25',
      '25 to 34',
      '35 to 44',
      '45 to 54',
      '55 to 64',
      '65 or over',
      "I'd rather not say",
    ],
  },
  {
    id: 'Q2', section: 1, type: 'dropdown',
    question: 'Which of these is closest to your current situation?',
    options: [
      'Recently made redundant / laid off',
      'Employed but exploring options',
      'Employed and wanting to build a side business',
      'I have a business and want to grow or develop it further',
      'Self-employed / freelancing',
      'Between jobs / actively job searching',
      'Unemployed and not sure what to do',
      'Student or recent graduate, just starting out',
      'Returning to work after a break (family, caring, health)',
      'Retired / semi-retired and want to do something new',
      'Retired / semi-retired and need more income',
    ],
    triggersFollowUp: {
      values: [
        'Employed but exploring options',
        'Between jobs / actively job searching',
        'Unemployed and not sure what to do',
      ],
      followUpId: 'Q2a',
    },
  },
  {
    id: 'Q2a', section: 1, type: 'single', conditional: true,
    question: 'Which of these feels closest to the truth for you right now?',
    options: [
      "I want another job, that's my focus",
      "I'm looking, but I'm not sure another job is what I want",
      "I'm looking because I feel I should, but I'd rather find another way",
      "I'd really like to work for myself or build something of my own",
      "I'm genuinely not sure, and that's part of why I'm here",
    ],
  },
  {
    id: 'Q3', section: 1, type: 'single',
    question: 'Which best describes your working experience so far?',
    options: [
      "I'm earlier in my working life, still building my experience",
      "I've built solid experience in one main field",
      "I've built deep, extensive expertise in one field over many years",
      "I've built experience across several different fields or roles",
      "I've run or helped run businesses or ventures myself",
    ],
  },
  {
    id: 'Q4', section: 1, type: 'single', primaryDriver: true,
    question: "Have you ever started something of your own: a business, a side project, freelancing, a market stall, a community initiative, anything? (It doesn't matter how big it got or whether it worked out.)",
    options: [
      'Yes, I run my own business or work for myself now',
      "Yes, I've run my own business or worked for myself in the past",
      "Yes, I've had a side project, freelance work, or small venture",
      "Yes, I started something, even if it didn't take off",
      "Not a venture, but I've initiated projects or change at work or in my community",
      "I've thought about it seriously, but haven't had the tools or support to start yet",
      "No, I haven't started something like that",
    ],
  },
  {
    id: 'Q5', section: 1, type: 'multi',
    question: 'What brings you to SPRING right now?',
    subtitle: 'Select all that apply.',
    options: [
      "I've been made redundant and need to figure out what's next",
      'I want to be my own boss',
      'I want to build income from what I know',
      'I want to build a business around an idea',
      'I have a specific idea I want to develop',
      "I'm employed but thinking about doing something different",
      'I want to understand my options before deciding anything',
      'Honestly, I have no idea, I need help working it out',
      'Someone I respect suggested I look into this',
    ],
  },
  {
    id: 'Q5a', section: 1, type: 'single',
    question: 'Thinking about your next step, which feels closest to what you want right now?',
    options: [
      'Build a business that could grow beyond me',
      'Use my skills and experience to work for myself, for example as a sole trader or contractor',
      'Find the right job or role for me',
      "I'm not sure yet, I'd like to explore all three",
    ],
  },
  {
    id: 'Q6', section: 1, type: 'single',
    preface: ['Before we go into the details, how are you actually doing right now? Not the professional version. The honest one.'],
    question: 'How are you feeling right now, honestly?',
    options: [
      'Genuinely excited',
      'Cautiously optimistic',
      'Mixed, hopeful but a lot going on',
      "Honestly, it's been hard",
      'Feeling quite lost right now',
      'Exhausted, this period has taken a lot out of me',
      'Anxious about the future',
      "I'd rather not say",
    ],
  },
  {
    id: 'Q7', section: 1, type: 'single', optional: true,
    preface: ["Optional, but an honest answer helps us support you best. You don't have to share anything you're not comfortable with."],
    question: "Is there anything happening in your life right now that might affect how much time or energy you have for this?",
    options: [
      'Nothing significant, I have good capacity right now',
      'A few things on my plate but manageable',
      'Quite a lot going on, time and energy are limited',
      'Going through something difficult right now',
      "I'd rather not say",
    ],
  },

  // ====================================================================
  // SECTION 2 — Mindset and Motivation
  // ====================================================================
  {
    id: 'Q8', section: 2, type: 'scale',
    question: 'Making my own decisions and being responsible for how things turn out matters a lot to me.',
    scaleMin: 1, scaleMax: 5,
    scaleLabels: { 1: 'not really important to me', 5: 'extremely important to me' },
  },
  {
    id: 'Q9', section: 2, type: 'scale',
    question: 'Having stability and a clear, steady structure to work within matters a lot to me.',
    scaleMin: 1, scaleMax: 5,
    scaleLabels: { 1: 'not really important to me', 5: 'extremely important to me' },
  },
  {
    id: 'Q10', section: 2, type: 'single',
    question: "Imagine something happens that genuinely wasn't your fault: a project gets cancelled, a client pulls out, plans fall through because of something completely outside your control. What's closest to how you'd actually respond?",
    options: [
      "I'd feel it, but pretty quickly start looking at what I can still do about it",
      "I'd need some time to process it before I could think about next steps",
      "I'd focus on understanding why it happened before deciding what to do",
      "I'd probably feel pretty stuck for a while; it's hard for me to move past things like that",
      "I'd want to talk it through with someone before knowing how I feel about it",
    ],
  },
  {
    id: 'Q11', section: 2, type: 'two_part',
    questionA: "Part A: When something needs doing and it isn't clearly anyone's job, which sounds most like you?",
    optionsA: [
      'I usually just get on with it myself',
      "I take the lead when I can see it's needed, and I'm just as happy to back someone else up",
      "I step in if it matters and no one else has",
      "I do my best work with a clear brief and a strong team around me",
      "I usually wait to see how things unfold before I act",
    ],
    questionB: "Part B: Tell us about a time you saw something that needed doing, and you just did it, without being asked. Work, home, community, anywhere.",
    guidedPrompts: ['what was it?', 'what made you step in?', 'what did you do?', 'how did it turn out?'],
    afterEncouragement: "Thank you for sharing that. You're doing really well, let's keep going.",
  },
  {
    id: 'Q12', section: 2, type: 'single',
    question: "Imagine you're several months into something that matters to you, and it's proving much harder than expected: slower progress, more setbacks than you planned for. What's closest to how you'd handle it?",
    options: [
      "I'd take a step back to properly assess whether this is still the right path, then decide",
      "I'd likely stop; if it's this hard, it's probably not going to work",
      "I'd keep going, but change my approach based on what I'm learning",
      "I'd push through exactly as planned; stopping would feel like failure",
      "I'd probably start doubting whether to continue, and want to talk it through with others",
    ],
  },
  {
    id: 'Q14', section: 2, type: 'single',
    question: "What's driving this more: wanting to escape something, or wanting to build something?",
    options: [
      'Mostly BUILD something',
      'A genuine mix of both',
      'Mostly ESCAPE something',
      'Not sure yet',
    ],
  },
  {
    id: 'Q15', section: 2, type: 'scale_with_text',
    question: 'How comfortable are you with uncertainty and not knowing what comes next?',
    scaleMin: 1, scaleMax: 5,
    scaleLabels: { 1: 'need to know the plan', 5: 'uncertainty is the adventure' },
    textPrompt: 'In a few words, what does uncertainty feel like for you?',
  },
  {
    id: 'Q16', section: 2, type: 'scale_with_options',
    question: 'Part A: On a scale of 1 to 10: How much do you genuinely WANT to do something different?',
    scaleMin: 1, scaleMax: 10,
    scaleLabels: { 1: 'Not really', 10: 'Absolutely, I want something new' },
    partB: {
      question: 'Part B: And what would "something different" look like for you?',
      options: [
        'Building on what I already do, like growing my business, my career or my skills',
        'A new direction that uses my skills and experience',
        'Something completely different',
        "I'm not sure yet",
      ],
    },
  },
  {
    id: 'Q17', section: 2, type: 'multi_exact',
    question: 'From the list, choose your top 3. What matters most to you in your working life?',
    subtitle: 'Choose exactly 3.',
    exactCount: 3,
    options: [
      'Autonomy',
      'Financial security',
      'Impact',
      'Flexibility',
      'Creative expression',
      'Recognition',
      'Growth',
      'Stability',
      'Connection',
      'Purpose',
      'Legacy/building something that lasts',
    ],
  },
  {
    id: 'Q13', section: 2, type: 'textarea', optional: true,
    question: "What's something you've learned about yourself in the last 12 months?",
    subtitle: 'Optional. Short. Text or voice. No examples.',
    inputMode: 'text_or_voice',
  },
  {
    id: 'Q18', section: 2, type: 'textarea',
    question: 'Success is deeply personal and looks different for everyone. What does success look like for you in 2 to 3 years\' time?',
    inputMode: 'text_speak_guided',
    showExamples: false,
  },
  {
    id: 'Q19', section: 2, type: 'single',
    question: "When there's no external deadline or accountability, what actually happens?",
    options: [
      'I set my own deadlines and stick to them',
      "I'm productive most of the time and create my own structure",
      'I deliver what I commit to, and focus on what really matters',
      'I work in bursts: very focused, then quieter',
      "I'm reliable once I start, but can find it hard to begin",
      'I do my best when someone helps keep me on track',
    ],
  },
  {
    id: 'Q20', section: 2, type: 'single',
    question: "Everyone taking a new direction weighs up what's at stake. When you think about what you'd be taking on, what's your biggest consideration?",
    options: [
      'The financial side: supporting myself/my family through it',
      "Whether I've got the right skills and experience yet",
      'The time it takes and whether it pays off',
      'What people close to me will think',
      'Honestly, quite a lot feels overwhelming right now',
      "I've weighed it up and I feel clear about what I'm taking on",
    ],
  },
  {
    id: 'Q21', section: 2, type: 'energises_drains',
    questionEnergises: 'Two parts: What genuinely energises you? (pick up to 3)',
    maxEnergises: 3,
    energisers: [
      'Building and leading a team',
      'Growing something bigger than myself',
      'Setting things up so they run without me',
      'Doing the hands-on work myself',
      'Being trusted as the expert',
      'Choosing my own clients and projects',
      'Being part of a team in an organisation',
      'Having a clear role and doing it really well',
      'Learning and progressing within an organisation',
      'Problem-solving',
      'Being creative, like designing, writing or making art',
      'Making things or hands-on, practical work',
      'Helping others learn and grow',
      'Helping customers or clients',
    ],
    questionDrains: 'What consistently drains you? (pick up to 2)',
    maxDrains: 2,
    drains: [
      'Admin and paperwork',
      'Repetitive tasks',
      'Working alone for long stretches',
      "Managing others' emotions",
      'Selling myself',
      'Uncertainty',
      'Slow progress',
      'Having someone else decide how I work',
    ],
  },

  // ====================================================================
  // SECTION 3 — Financial Situation
  // ====================================================================
  {
    id: 'Q22', section: 3, type: 'single',
    question: 'Thinking about your everyday living costs, like rent or mortgage, power and food, which is closest for you right now?',
    options: [
      'I cover them on my own',
      'We share them (with a partner, [family_word] or others I live with)',
      'Someone else mainly covers them (a partner, parents or [family_word])',
      "I'd rather not say",
    ],
    countryWords: { NZ: 'wh\u0101nau', AU: 'family', UK: 'family', other: 'family' },
  },
  {
    id: 'Q22a', section: 3, type: 'single', conditional: true,
    showUnless: { questionId: 'Q22', value: "I'd rather not say" },
    question: 'If your own income stopped or changed for a while, could anyone else help cover those costs?',
    options: [
      'Yes, comfortably',
      'Yes, for a while',
      'Probably not',
      "I'm not sure",
      "I'd rather not say",
    ],
  },
  {
    id: 'Q23', section: 3, type: 'single',
    question: 'Does anyone rely on your income right now?',
    subtitle: 'This could be children, a partner, parents or [family_word], here or overseas.',
    options: [
      'No, no one relies on my income',
      'Yes, I\'m a solo parent',
      'Yes, children (shared with a partner or co-parent)',
      'Yes, other adults, like a partner, parents or [family_word]',
      'Yes, both children and other adults',
      "I'd rather not say",
    ],
    countryWords: { NZ: 'wh\u0101nau', AU: 'family', UK: 'family', other: 'family' },
    triggersFollowUp: {
      anyYes: true,
      followUpId: 'Q23a',
    },
  },
  {
    id: 'Q23a', section: 3, type: 'single', conditional: true,
    question: 'How many people rely on your income (children and adults), not counting you?',
    options: [
      '1',
      '2',
      '3',
      '4',
      '5 or more',
      "I'd rather not say",
    ],
  },
  {
    id: 'Q24', section: 3, type: 'financial_calculator',
    preface: [
      "Take a moment to think about where your finances sit right now. There's no right or wrong answer, and a rough idea is all we need.",
      "Step 1: First, think about roughly how much you need each month to cover your main needs: rent or mortgage, power and gas, food, loan repayments, and the other bills you can't avoid.",
      'Step 2: With that monthly expenses figure in mind, think about:',
      { bullets: [
        'your available savings, with any redundancy payment added to what you already have ([retirement_word])',
        "any income still coming in, like a partner's wages or a benefit",
      ] },
    ],
    question: 'Roughly how long would your money last?',
    subtitle: 'This helps you see your possible timeline for your next step.',
    retirementWords: { NZ: 'leave out KiwiSaver', AU: 'leave out your super', UK: 'leave out your pension', other: 'leave out retirement savings' },
    options: [
      'A couple of weeks or less',
      'About 1 month',
      '2 months',
      '3 months',
      '4 months',
      '5 months',
      '6 months',
      '7 months',
      '8 months',
      '9 months',
      '10 months',
      '11 months',
      '12 months or more',
      'My main needs are already covered, by my income or by others',
      "I'm not sure yet",
      "Someone else looks after our money, so I'm not sure of the figures",
      "I'd rather not say",
    ],
  },
  {
    id: 'Q25', section: 3, type: 'multi',
    question: "Money can bring up all sorts of feelings, and every one of them is normal. How do you feel about your finances at the moment?",
    subtitle: "Tick all that apply. There's no right or wrong answer.",
    options: [
      'Calm, I feel comfortable with where things are',
      'Good, I know I have support and back-up',
      'Mostly OK, with a few little worries',
      'OK for now, but concerned about the future',
      "Uneasy, it's on my mind a lot",
      "Anxious, it's hard to stop thinking about it",
      "Overwhelmed, I don't know where to start",
      "I'd rather not say",
    ],
    exclusiveOption: "I'd rather not say",
  },
  {
    id: 'Q26', section: 3, type: 'multi',
    question: 'If you wanted to start something of your own and needed some money to get it going, where could it come from?',
    subtitle: "Tick all that apply. You don't need to know how much yet.",
    options: [
      'My own savings, set aside from my living costs',
      'A partner, family or wh\u0101nau',
      'Borrowing',
      "I'm not sure yet",
      'Nowhere right now',
      "I'd rather not say",
    ],
    exclusiveOptions: ['Nowhere right now', "I'd rather not say"],
  },
  {
    id: 'Q27', section: 3, type: 'single', conditional: true,
    showUnless: { questionId: 'Q26', values: ['Nowhere right now', "I'd rather not say"] },
    question: 'Thinking about the money you have, or could draw on, how would you feel about using some of it to get something of your own started?',
    options: [
      "Ready to use it, that's what it's there for",
      'Willing to use some, cautiously',
      "Nervous about touching it, even though I could",
      "I'd really rather not use it at all",
    ],
  },

  // ====================================================================
  // SECTION 4 — Skills Inventory
  // ====================================================================
  {
    id: 'Q28', section: 4, type: 'structured_depth',
    question: "Tell us about something you've done that you're proud of.",
    subtitle: "It doesn't need to be your biggest achievement. Paid work, volunteering or your personal life all count.",
    fields: [
      { key: 'role', label: 'Role or context' },
      {
        key: 'duration', label: 'How long',
        inputType: 'dropdown',
        options: ['Under 1 year', '1 to 3 years', '3 to 5 years', '5 to 10 years', '10 years or more'],
      },
      { key: 'scope', label: 'What you were responsible for' },
      { key: 'what_you_did', label: 'What you did' },
      { key: 'result', label: 'What difference did it make? (optional)', optional: true },
    ],
    examples: [
      {
        label: 'Example 1 (paid work)',
        role: 'Team leader at a regional freight company',
        duration: '5 to 10 years',
        scope: 'A team of eight and the daily delivery schedule',
        what_you_did: 'I rebuilt the schedule so drivers had fewer wasted trips, and trained two new team leaders',
        result: 'Deliveries arrived on time more often, and the team was less stressed',
      },
      {
        label: 'Example 2 (volunteering)',
        role: 'Volunteer treasurer for a local sports club',
        duration: '3 to 5 years',
        scope: "The club's accounts, membership fees and yearly budget",
        what_you_did: 'I moved the club from paper records to a simple spreadsheet and set up online payment for fees',
        result: 'Fees came in faster, and the committee could see where the money was going',
      },
      {
        label: 'Example 3 (personal life)',
        role: 'Organising a big family celebration',
        duration: 'Under 1 year',
        scope: 'The venue, food and travel for 80 guests, and the budget',
        what_you_did: 'I set the budget, shared out the jobs, and kept everyone updated in one group chat',
        result: 'It came in on budget, and everyone had a great day',
      },
    ],
  },
  {
    id: 'Q29', section: 4, type: 'skills_checklist',
    question: 'Which two of these are your strongest?',
    skills: [
      'Organising and managing projects',
      'Leading and developing people',
      'Sales and marketing',
      'Money and financial management',
      'Technology and digital',
      'Creative and design',
      'Communication and writing',
      'Customer and client care',
      'Hands-on, technical or trade skills',
    ],
    maxSkills: 2,
    usedWhere: {
      question: 'Where have you mostly used these?',
      subtitle: 'Tick all that apply.',
      options: [
        'Paid work',
        'Volunteering',
        'Running a household',
        'Community, sports or board roles',
      ],
    },
    helpWith: {
      question: 'And which one would you most like help with?',
    },
    examplePrompt: "Give one example of when you've used your top skill.",
  },
  {
    id: 'Q30', section: 4, type: 'qualifications',
    question: 'Do you hold any formal qualifications, certifications, licences, or trade registrations relevant to your skills or experience?',
    options: ['Yes', 'No'],
    repeatableFields: [
      { key: 'name', label: 'Qualification/credential name' },
      { key: 'issuer', label: 'Issuing body' },
      { key: 'year', label: 'Year obtained' },
    ],
  },
  {
    id: 'Q31', section: 4, type: 'two_yesno',
    questionA: 'Have you been paid to do the kind of work you just described?',
    optionsA: [
      'Yes, in a job',
      'Yes, through my own business, freelancing or side work',
      'Yes, both',
      'Not yet',
    ],
    questionB: "Has someone told you directly that you're good at it, like a boss, client, colleague or mentor?",
    optionsB: [
      'Yes',
      'Not yet',
    ],
  },
];

/* ── Ordering: Q13 sits just before Q18 (handled in the array above) ── */

export default QUESTIONS;

/* Helpers */
export const QUESTION_MAP = Object.fromEntries(QUESTIONS.map(q => [q.id, q]));
export const TOTAL_QUESTION_COUNT = QUESTIONS.filter(q => !q.conditional).length;
